import React, { useState, useEffect } from 'react';
import apiDefinitions from "../../../../core/api/apiDefinitions";

const AddVehicleCondition: React.FC = () => {
  const [vehicleConditionData, setVehicleConditionData] = useState<any>(null);
  const [isVehicleConditionLoading, setIsVehicleConditionLoading] = useState(true);
  const [selectedVehicleConditionId, setSelectedVehicleConditionId] = useState("");
  const [newVehicleConditionName, setNewVehicleConditionName] = useState("");
  const [formErrors, setFormErrors] = useState({
    vehicleConditionName: "",
    selectedVehicleCondition: "",
  });
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openFail, setOpenFail] = useState(false);
  const [errorMessage, setErrorMessage] = useState("Operation Failed");

  const handleCloseSuccess = () => setOpenSuccess(false);
  const handleCloseFail = () => setOpenFail(false);

  // Auto-dismiss success notification
  useEffect(() => {
    if (openSuccess) {
      const timer = setTimeout(() => setOpenSuccess(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [openSuccess]);

  // Fetch vehicle conditions on component mount
  useEffect(() => {
    fetchVehicleConditions();
  }, []);

  const fetchVehicleConditions = () => {
    setIsVehicleConditionLoading(true);
    apiDefinitions.getAllVehicleConditions()
      .then((response) => {
        if (response.data.status === "S1000" && response.data.data) {
          setVehicleConditionData(response.data);
        } else {
          setErrorMessage(response.data.statusDescription || "Failed to load vehicle conditions");
          setOpenFail(true);
        }
        setIsVehicleConditionLoading(false);
      })
      .catch((error) => {
        const message = error.response?.data?.statusDescription || error.response?.data?.message || "Failed to load vehicle conditions: Server error";
        setErrorMessage(message);
        setOpenFail(true);
        setIsVehicleConditionLoading(false);
      });
  };

  const validateVehicleConditionForm = (name: string) => {
    const errors = { vehicleConditionName: "", selectedVehicleCondition: "" };
    let isValid = true;

    if (!name.trim()) {
      errors.vehicleConditionName = "Vehicle condition name is required";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const validateSelectedVehicleCondition = () => {
    const errors = { vehicleConditionName: "", selectedVehicleCondition: "" };
    let isValid = true;

    if (!selectedVehicleConditionId) {
      errors.selectedVehicleCondition = "Please select a vehicle condition";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleAddVehicleConditionSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateVehicleConditionForm(newVehicleConditionName)) return;

    apiDefinitions.createVehicleCondition({ conditionName: newVehicleConditionName })
      .then((response: { data: any }) => {
        if (response.data.status === "S1000") {
          setOpenSuccess(true);
          setNewVehicleConditionName("");
          fetchVehicleConditions();
        } else {
          setErrorMessage(response.data.statusDescription || "Vehicle condition creation failed");
          setOpenFail(true);
        }
      })
      .catch((error: any) => {
        const message = error.response?.data?.statusDescription || error.response?.data?.message || "Failed to create vehicle condition: Server error";
        setErrorMessage(message);
        setOpenFail(true);
      });
  };

  const handleDeleteVehicleCondition = () => {
    if (!validateSelectedVehicleCondition()) return;

    apiDefinitions.deleteVehicleCondition(parseInt(selectedVehicleConditionId))
      .then((response) => {
        if (response.data.status === "S1000") {
          setOpenSuccess(true);
          setSelectedVehicleConditionId("");
          fetchVehicleConditions();
        } else {
          setErrorMessage(response.data.statusDescription || "Vehicle condition deletion failed");
          setOpenFail(true);
        }
      })
      .catch((error) => {
        const message = error.response?.data?.statusDescription || error.response?.data?.message || "Failed to delete vehicle condition: Server error";
        setErrorMessage(message);
        setOpenFail(true);
      });
  };

  const handleVehicleConditionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedVehicleConditionId(e.target.value);
    setFormErrors((prev) => ({ ...prev, selectedVehicleCondition: "" }));
  };

  return (
    <>
      <div className="card">
        <div className="card-body pb-0">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h4 className="fs-20 mb-0">Add Vehicle Condition</h4>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="row" style={{ gap: '30px' }}>
                <div className="col-md-5">
                  <div className="mb-4">
                    <label className="col-form-label mb-2"><h5>Vehicle Condition</h5></label>
                    <div className="d-flex align-items-center">
                      <input
                        type="text"
                        className="form-control"
                        style={{ width: '300px', padding: '10px 15px' }}
                        value={newVehicleConditionName}
                        onChange={(e) => setNewVehicleConditionName(e.target.value)}
                        placeholder="Enter vehicle condition name"
                      />
                      <button
                        type="button"
                        className="btn btn-success ms-3"
                        data-bs-toggle="modal"
                        data-bs-target="#vehicle-condition-modal"
                        style={{ padding: '10px 12px', whiteSpace: 'nowrap', display: 'inline-block' }}
                      >
                        Add Condition
                      </button>
                    </div>
                    {formErrors.vehicleConditionName && (
                      <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.vehicleConditionName}</div>
                    )}
                    <select
                      className="form-select mt-3"
                      value={selectedVehicleConditionId}
                      onChange={handleVehicleConditionChange}
                      style={{ width: '300px', padding: '10px' }}
                    >
                      <option value="">Select Vehicle Condition</option>
                      {isVehicleConditionLoading ? (
                        <option disabled>Loading...</option>
                      ) : (
                        vehicleConditionData?.data?.map((vehicleCondition: any) => (
                          <option key={vehicleCondition.id} value={vehicleCondition.id}>{vehicleCondition.conditionName}</option>
                        ))
                      )}
                    </select>
                    {formErrors.selectedVehicleCondition && (
                      <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.selectedVehicleCondition}</div>
                    )}
                    <div className="d-flex gap-2 mt-3">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={handleDeleteVehicleCondition}
                        style={{ padding: '8px 16px' }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Vehicle Condition Modal */}
      <div className="modal fade" id="vehicle-condition-modal" tabIndex={-1} aria-labelledby="add-vehicle-condition" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="add-vehicle-condition">Add New Vehicle Condition</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleAddVehicleConditionSubmit}>
                <div className="mb-3">
                  <label htmlFor="newVehicleConditionName" className="form-label">Vehicle Condition Name</label>
                  <input
                    type="text"
                    className="form-control"
                    id="newVehicleConditionName"
                    value={newVehicleConditionName}
                    onChange={(e) => setNewVehicleConditionName(e.target.value)}
                    placeholder="Enter vehicle condition name (e.g., Excellent)"
                    style={{ width: '100%', padding: '10px 15px' }}
                  />
                  {formErrors.vehicleConditionName && (
                    <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.vehicleConditionName}</div>
                  )}
                </div>
                <button type="submit" className="btn btn-success" data-bs-dismiss="modal" style={{ padding: '10px 20px' }}>
                  Add Vehicle Condition
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Success Notification */}
      {openSuccess && (
        <div
          className="alert alert-success alert-dismissible fade show"
          role="alert"
          style={{ position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 1100 }}
        >
          Operation successful
          <button
            type="button"
            className="btn-close"
            onClick={handleCloseSuccess}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Error Notification */}
      {openFail && (
        <div
          className="alert alert-danger alert-dismissible fade show"
          role="alert"
          style={{ position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 1100 }}
        >
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={handleCloseFail}
            aria-label="Close"
          ></button>
        </div>
      )}
    </>
  );
};

export default AddVehicleCondition;